import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PlatformService } from '../services/platform.service';
import { ToastService } from '../services/toast.service';
import { SKIP_AUTH_REFRESH } from '../auth/auth.context';

function withAuth(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  if (!token) return req;
  // Contexto de loja (futuro): quando existir, anexar aqui
  //   .set('X-Store-Id', storeContext.currentStoreId())
  // Não implementar agora — deixado documentado propositalmente.
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Interceptor da API da plataforma ativa:
 * - anexa Bearer <accessToken> nas chamadas ao apiBaseUrl ativo;
 * - em 401, dispara UM refresh compartilhado (fila) e retenta;
 * - em 403, avisa "Sem permissão" sem deslogar.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const platform = inject(PlatformService);
  const toast = inject(ToastService);

  const base = platform.apiBaseUrl();
  // Só mexemos em chamadas à API da plataforma ativa.
  if (!base || !req.url.startsWith(base)) {
    return next(req);
  }

  const skipRefresh = req.context.get(SKIP_AUTH_REFRESH);
  const authed = withAuth(req, auth.accessToken);

  return next(authed).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) return throwError(() => err);

      // 403: autorização é do back; não desloga, apenas avisa.
      if (err.status === 403) {
        toast.warning('Sem permissão para esta ação');
        return throwError(() => err);
      }

      // 401 fora da própria chamada de refresh: renova (fila) e retenta.
      if (err.status === 401 && !skipRefresh) {
        return auth.refreshAccessToken().pipe(
          switchMap((newToken) => next(withAuth(req, newToken))),
          catchError((refreshErr) => throwError(() => refreshErr)),
        );
      }

      return throwError(() => err);
    }),
  );
};
